import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import "rc-time-picker/assets/index.css";
import { Text } from "shared/components/Text";
import { TextInput } from "shared/components/TextInput";
import TimePicker from "rc-time-picker";
import { TextAreaInput } from "shared/components/TextAreaInput";
import { Button } from "shared/components/Button";
import { signUpForForIntegrations } from "../eventsApi";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { getLocations } from "store/selectors";
import css from "./styles.module.scss";
import { appStore } from "store";

const emailPattern =
  /^[a-zA-Z0-9.a-zA-Z0-9.!#$%&'*+-/=?^_`{|}~]+@[a-zA-Z0-9.-]+\.[a-zA-Z]+$/;

export const IntegrationForm = ({ setIsSubmitLoading, city }) => {
  const { t, i18n } = useTranslation();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { locations } = appStore.useState(getLocations);
  const countryId = process.env.REACT_APP_COUNTRY_ID || "pl";

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.error("Execute recaptcha not yet available");
      return;
    }

    const token = await executeRecaptcha("submitEventsPagesForm");
    return token;
  }, [executeRecaptcha]);

  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [peopleCount, setPeopleCount] = useState("");
  const [time, setTime] = useState("");
  const [date, setDate] = useState("");
  const [expectations, setExpectations] = useState("");

  const [companyNameError, setCompanyNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [peopleCountError, setPeopleCountError] = useState("");
  const [timeError, setTimeError] = useState("");
  const [dateError, setDateError] = useState("");
  const [expectationsError, setExpectationsError] = useState("");
  const [connectionError, setConnectionError] = useState("");

  const [wasSuccessful, setWasSuccessful] = useState(false);

  const onSubmit = async () => {
    setConnectionError("");
    setCompanyNameError("");
    setPhoneError("");
    setEmailError("");
    setPeopleCountError("");
    setTimeError("");
    setDateError("");
    setExpectationsError("");
    let wrongSubmit = false;

    const emailTrimmed = email.trim();
    if (!emailTrimmed) {
      setEmailError(t("form.errors.emptyEmail"));
      wrongSubmit = true;
    } else if (!emailPattern.test(emailTrimmed)) {
      setEmailError(t("form.errors.incorrectEmail"));
      wrongSubmit = true;
    } else if (emailTrimmed.length > 64) {
      setEmailError(t("form.errors.emailTooLong"));
      wrongSubmit = true;
    }

    if (!companyName) {
      setCompanyNameError(t("form.errors.emptyName"));
      wrongSubmit = true;
    } else if (companyName.length > 64) {
      setCompanyNameError(t("form.errors.nameTooLong"));
      wrongSubmit = true;
    }

    if (!phone) {
      setPhoneError(t("form.errors.emptyPhone"));
      wrongSubmit = true;
    } else if (phone.length < 7) {
      setPhoneError(t("form.errors.incorrectPhone"));
      wrongSubmit = true;
    }

    if (!peopleCount) {
      setPeopleCountError(t("form.errors.emptyPeopleCount"));
      wrongSubmit = true;
    } else {
      const count = parseInt(peopleCount, 10);
      if (isNaN(count) || count < 0) {
        setPeopleCountError(t("form.errors.invalidPeopleCount"));
        wrongSubmit = true;
      }
    }

    if (!date) {
      setDateError(t("form.errors.emptyDate"));
      wrongSubmit = true;
    } else {
      const selectedDate = new Date(date);
      const currentDate = new Date();

      if (selectedDate < currentDate) {
        setDateError(t("form.errors.pastDate"));
        wrongSubmit = true;
      }
    }

    if (!time) {
      setTimeError(t("form.errors.emptyTime"));
      wrongSubmit = true;
    }

    if (expectations.length > 500) {
      setExpectationsError(t("form.errors.expectationsTooLong"));
      wrongSubmit = true;
    }

    if (wrongSubmit) {
      return;
    }

    setIsSubmitLoading(true);

    try {
      const token = await handleReCaptchaVerify();
      await signUpForForIntegrations({
        name: companyName,
        phone: phone,
        email: email,
        peopleCount: peopleCount,
        time: time,
        date: date,
        expectations: expectations,
        token: token,
        city: locations.cities[city].cityOptions.nameKey,
        language: i18n.language,
        countryId: countryId,
      });

      setWasSuccessful(true);
      setCompanyName("");
      setPhone("");
      setEmail("");
      setPeopleCount("");
      setTime("");
      setDate("");
      setExpectations("");
    } catch (e) {
      console.log(e);
      setConnectionError(t("form.errors.connectionError"));
    }
    setIsSubmitLoading(false);
  };

  return (
    <div className={css.contactSection}>
      {!wasSuccessful ? (
        <>
          <Text
            type="white"
            bold
            className={css.contactSectionText}
            alignCenter
            secondaryFamily
            size="bodyLead"
          >
            {t("integrationPage.titles.form")}
          </Text>
          <Text type="white" alignCenter size="body">
            {t("integrationPage.body.form")}
          </Text>
          <form className={css.form}>
            <div className={css.formInputsContainer}>
              <div className={css.formColumOneContainer}>
                <div className={css.formTextInputContainer}>
                  <TextInput
                    placeholder={t("eventsPagesForm.companyName")}
                    value={companyName}
                    setValue={setCompanyName}
                    mode="classes"
                    error={companyNameError}
                  />
                </div>
                <div className={css.formTextInputContainer}>
                  <TextInput
                    placeholder={t("eventsPagesForm.email")}
                    value={email}
                    setValue={setEmail}
                    type="email"
                    mode="classes"
                    error={emailError}
                  />
                </div>
                <div className={css.formTextInputContainer}>
                  <TextInput
                    placeholder={t("eventsPagesForm.phone")}
                    value={phone}
                    setValue={setPhone}
                    type="tel"
                    mode="classes"
                    error={phoneError}
                  />
                </div>
              </div>
              <div className={css.formColumTwoContainer}>
                <div className={css.formTextInputContainer}>
                  <TextInput
                    placeholder={t("eventsPagesForm.date")}
                    value={date}
                    setValue={setDate}
                    type="date"
                    mode="classes"
                    error={dateError}
                  />
                </div>
                <div className={css.formTextInputContainer}>
                  <TimePicker
                    placeholder={t("eventsPagesForm.time")}
                    className={cn(css.timePicker, {
                      [css.errorInput]: timeError !== "",
                    })}
                    minuteStep={
                      locations.cities[city].cityOptions.reservationTimeStep
                    }
                    showSecond={false}
                    onChange={(e) => setTime(`${e.format("HH:mm")}`)}
                  />
                  {timeError && <p className={css.errorText}>{timeError}</p>}
                </div>
                <div className={css.formTextInputContainer}>
                  <TextInput
                    placeholder={t("eventsPagesForm.peopleCount")}
                    value={peopleCount}
                    setValue={setPeopleCount}
                    type="number"
                    mode="classes"
                    error={peopleCountError}
                  />
                </div>
              </div>
            </div>
            <div className={css.formTextInputContainer}>
              <TextAreaInput
                placeholder={t("eventsPagesForm.expectations")}
                value={expectations}
                setValue={setExpectations}
                error={expectationsError}
              />
            </div>
          </form>
          {connectionError && (
            <Text
              type="red"
              className={css.connectionErrorText}
              alignCenter
              size="body"
            >
              {connectionError}
            </Text>
          )}
          <Button
            text={t("eventsPagesForm.submitButton")}
            className={css.submitButton}
            textClassname={css.submitButtonText}
            onClick={onSubmit}
          />
        </>
      ) : (
        <div className={css.thankYouContainer}>
          <Text type="pink" alignCenter bold secondaryFamily size="bodyLead">
            {t("eventsPagesForm.thankYou")}
          </Text>
          <Button
            text={t("eventsPagesForm.contactAgainButton")}
            className={css.submitButton}
            textClassname={css.submitButtonText}
            onClick={() => setWasSuccessful(false)}
          />
        </div>
      )}
    </div>
  );
};
